import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import {
    ActionButton,
    ContextualMenuItemType,
    IButtonProps,
    ICalloutProps,
    IconButton,
    IContextualMenuItem,
    IContextualMenuProps,
    IContextualMenuSection,
    ISearchBox,
    SearchBox,
} from '@fluentui/react'
import { getPartyIconName } from '../../helpers/iconHelper'
import { addFileParty, getPartyChildSuggestions } from '../../actions/filePartyActions'
import { getPartyList } from '../../actions/partyActions'
import { regexSearchable } from '../../helpers/regexHelper'
import { DispatchProp, Store } from '../../reducers'
import { DataPromise, shouldDispatchLoad } from '../../context/AxiosContext'
import { FilePartyAction, FilePartyRole, MainDto } from '../../config/filePartyConfig'
import { ListItemDto } from '../../config/partyConfig'
import { AddPartyProps, PartyType } from './Party/PartyForm'
import { FilePartyRoleDto } from '../../context/GlobalContext'

interface IConnect {
    parties: DataPromise<ListItemDto[]>
}

export interface IPartyConnectButtonProps extends IButtonProps {
    fileId: string
    parent?: MainDto
    menuPropsTitle?: string
    roles: FilePartyRoleDto[]
}

interface IState {
    roleSection?: IContextualMenuSection
    partySection?: IContextualMenuSection
}

type Props = IConnect &
    DispatchProp &
    IPartyConnectButtonProps &
    RouteComponentProps & {
        translateFunction: (key: string) => string
        reset?: () => void
    }

class PartyConnectButton extends React.Component<Props, IState> {
    public state: IState = {}
    private searchBoxRef = React.createRef<ISearchBox>()
    private partyItems: IContextualMenuItem[]
    private suggestedPartyItems: IContextualMenuItem[]

    private roleSection: IContextualMenuSection = {
        key: 'role-section-props',
        topDivider: true,
        items: [] as IContextualMenuItem[],
    }

    private partySection: IContextualMenuSection = {
        key: 'party-section-props',
        topDivider: true,
        items: [],
    }

    public componentDidMount(): void {
        this.componentDidUpdate()

        // The presence of a parent ID indicates that we are adding children to a main party.
        if (this.props.parent) {
            // Do not use the global redux state, because multiple instances with different suggestions can exist at the
            // same entity.
            getPartyChildSuggestions(this.props.parent.pivotId).payload.then(response => {
                this.suggestedPartyItems = response.data.map((party: ListItemDto) => this.mapParty(party))
            })
        }

        this.initState()
    }

    public componentDidUpdate(): void {
        this.populateRoles()

        if (shouldDispatchLoad(this.props.parties)) {
            this.props.dispatch(getPartyList())
        } else {
            // Render the items to be shown in the contextual menu.
            this.partyItems = this.props.parties.data
                .filter(
                    (party: ListItemDto) =>
                        // If a parent is set, we only allow natural persons.
                        !this.props.parent || party.type === PartyType.Natural,
                )
                .map((party: ListItemDto) => this.mapParty(party))
        }
    }

    public render(): React.ReactNode {
        // Render the button that contains the contextual menu.
        // noinspection JSUnusedGlobalSymbols
        const Button = this.props.text !== null ? ActionButton : IconButton
        return (
            <Button
                key="party-connect-button"
                text={this.props.translateFunction('file.pivot.party.add')}
                iconProps={{ iconName: 'AddFriend' }}
                // Remove the menu chevron.
                menuIconProps={{ iconName: '' }}
                {...this.props}
                menuProps={
                    {
                        title: this.props.menuPropsTitle || this.props.translateFunction('file.pivot.party.add'),
                        // delayUpdateFocusOnHover and shouldFocusOnMount must have these values in order to allow an
                        // override on the item that will get the focus, so we can focus the search box instead.
                        delayUpdateFocusOnHover: true,
                        shouldFocusOnMount: false,
                        calloutProps: {
                            // Make sure that the search box is focused in the last event triggered during rendering.
                            onPositioned: () => this.searchBoxRef?.current?.focus(),
                        } as ICalloutProps,
                        // We can have two sections, however when sectionProps is null, it will be hidden from display.
                        items: [
                            {
                                key: 'role',
                                itemType: ContextualMenuItemType.Section,
                                sectionProps: this.state.roleSection,
                            },
                            {
                                key: 'party',
                                itemType: ContextualMenuItemType.Section,
                                sectionProps: this.state.partySection,
                            },
                        ] as IContextualMenuItem[],
                        onMenuOpened: () => this.initState(),
                    } as IContextualMenuProps
                }
            />
        )
    }

    /**
     * If a parent ID exists, we only have to show the parties in the initial state, not the roles.
     */
    private initState() {
        if (!this.props.parent) {
            this.setState({
                roleSection: this.roleSection,
                partySection: null,
            })
        } else {
            this.setState({
                partySection: this.getPartySection(),
            })
        }
    }

    /**
     * Get all parties that match the filter query in the search box.
     * @param query
     * @private
     */
    private filterPartyItems(query?: string) {
        query = query !== null ? regexSearchable(query) : ''
        this.setState({
            partySection: this.getPartySection(
                query.length >= 3
                    ? this.partyItems.filter((item: IContextualMenuItem) => item.text && regexSearchable(item.text).includes(query))
                    : undefined,
            ),
        })
    }

    private getPartySection(items?: IContextualMenuItem[]): IContextualMenuSection {
        const searchItem: IContextualMenuItem = {
            key: 'search-party',
            onRender: () => (
                <>
                    {!this.props.parent && (
                        <ActionButton
                            iconProps={{ iconName: 'BusinessCenterLogo' }}
                            onClick={() => this.props.history.push(`${this.props.match.url}/legal/add`, this.getAddPartyProps())}
                        >
                            {this.props.translateFunction('file.pivot.party.add.legal')}
                        </ActionButton>
                    )}
                    <ActionButton
                        iconProps={{ iconName: 'Contact' }}
                        onClick={() => this.props.history.push(`${this.props.match.url}/natural/add`, this.getAddPartyProps())}
                    >
                        {this.props.translateFunction('file.pivot.party.add.person')}
                    </ActionButton>
                    <SearchBox
                        placeholder={this.props.translateFunction('file.pivot.party.search')}
                        componentRef={this.searchBoxRef}
                        underlined
                        onChange={(event?: React.ChangeEvent<HTMLInputElement>, query?: string) => this.filterPartyItems(query)}
                    />
                </>
            ),
        }

        const placeholderInstructions: IContextualMenuItem = {
            key: 'placeholder-instructions',
            iconProps: { iconName: 'TextField' },
            text: this.props.translateFunction('file.pivot.party.search.instructions'),
            disabled: true,
        }

        const placeholderNoResults: IContextualMenuItem = {
            key: 'placeholder-no-results',
            iconProps: { iconName: 'Error' },
            text: this.props.translateFunction('file.pivot.party.search.no_content'),
            disabled: true,
        }

        return {
            ...this.partySection,
            items: [
                searchItem,
                ...(!items ? [placeholderInstructions] : items.length === 0 ? [placeholderNoResults] : items),
                ...(this.suggestedPartyItems && this.suggestedPartyItems.length > 0
                    ? [
                          {
                              key: 'suggested-divider',
                              itemType: ContextualMenuItemType.Divider,
                          },
                          {
                              key: 'suggested-header',
                              itemType: ContextualMenuItemType.Header,
                              text: this.props.translateFunction('file.pivot.party.suggestions'),
                          },
                          ...this.suggestedPartyItems,
                      ]
                    : []),
            ],
        }
    }

    /**
     * Populate the roles section of the dropdown.
     * @private
     */
    private populateRoles() {
        const selectedRole = React.createRef()
        const roles: IContextualMenuItem[] = this.props.roles
            .filter(role => role.type)
            .map(role => ({
                key: role.type,
                text: role.label,
            }))
        this.roleSection.items = roles.map((item: IContextualMenuItem) => ({
            ...item,
            checked: false,
            iconProps: {
                iconName: 'Checkbox',
            },
            // When a user selects a role from the contextual menu.
            onClick: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, item: IContextualMenuItem) => {
                if (item.checked) {
                    // If a role has been unset, return to the initial state, and display the role selection list.
                    this.initState()
                } else {
                    // Drill down in the menu and show a search bar with parties.
                    this.setState(
                        {
                            roleSection: {
                                ...this.roleSection,
                                items: [
                                    {
                                        ...item,
                                        checked: true,
                                        componentRef: selectedRole,
                                        iconProps: {
                                            iconName: 'CheckboxCompositeReversed',
                                        },
                                    },
                                ] as IContextualMenuItem[],
                            } as IContextualMenuSection,
                            partySection: this.getPartySection(),
                        },
                        () => this.searchBoxRef?.current?.focus(),
                    )
                }
                event.preventDefault()
            },
            onBlur: () => this.searchBoxRef?.current?.focus(),
        }))
    }

    /**
     * Populate the parties section of the dropdown.
     * @param party
     * @private
     */
    private mapParty(party: ListItemDto): IContextualMenuItem {
        return {
            key: party.id,
            text: party.displayName,
            iconProps: { iconName: getPartyIconName(party) },
            // Create the relation by dispatching the appropriate event.
            onClick: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, party?: IContextualMenuItem) => {
                ;(async () => {
                    await this.props.dispatch(
                        addFileParty({
                            fileId: this.props.fileId,
                            partyId: party.key,
                            role: this.getSelectedRole(),
                            ...(this.props.parent ? { parentId: this.props.parent.pivotId } : {}),
                        }),
                    )
                    // Make sure the changes propagate afterwards.
                    await this.props.dispatch({ type: FilePartyAction.ResetOverview })
                    this.props.reset?.()
                })()
            },
        }
    }

    /**
     * @private Get the params to construct the action button urls with.
     */
    private getAddPartyProps(): AddPartyProps {
        return {
            parentId: this.props.parent?.pivotId,
            role: this.getSelectedRole(),
        }
    }

    private getSelectedRole(): FilePartyRole | string {
        if (this.props.parent) {
            return this.props.parent.role
        } else {
            // Get the selected role from the role state.
            const value: string = this.state.roleSection?.items.find((role: IContextualMenuItem) => role.checked).key
            // Find the appropriate key in the FilePartyRole enum.
            const key: string = Object.keys(FilePartyRole).find((key: string) => FilePartyRole[key] === value)
            // Return the raw value if the key does not exist in the file party role enum.
            return key ? FilePartyRole[key] : value
        }
    }
}

export default withRouter(
    connect<IConnect, DispatchProp>((store: Store) => ({
        parties: store.parties.list,
    }))(PartyConnectButton),
)
